import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, X, RefreshCw, Download, Layers } from "lucide-react";
import Topbar from "@/components/layout/Topbar";
import Widget from "@/components/widgets/Widget";
import { api, API, getToken, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";

const TABS_KEY = "hb_open_dashboards";
const ACTIVE_KEY = "hb_active_tab";

export default function DashboardWorkspace() {
  const [tabs, setTabs] = useState([]);
  const [active, setActive] = useState(null);
  const [rendered, setRendered] = useState({}); // dashboard_id -> render payload
  const [loading, setLoading] = useState(false);
  const [picker, setPicker] = useState(false);
  const [available, setAvailable] = useState([]);

  // Load tabs from localStorage
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(TABS_KEY) || "[]");
      setTabs(saved);
      const lastActive = localStorage.getItem(ACTIVE_KEY);
      setActive(saved.some((t) => t.id === lastActive) ? lastActive : saved[0]?.id || null);
    } catch {
      setTabs([]);
    }
  }, []);

  // Persist tabs
  useEffect(() => {
    localStorage.setItem(TABS_KEY, JSON.stringify(tabs));
  }, [tabs]);
  useEffect(() => {
    if (active) localStorage.setItem(ACTIVE_KEY, active);
  }, [active]);

  // Render the active tab
  useEffect(() => {
    if (!active || rendered[active]) return;
    setLoading(true);
    api
      .get(`/my-dashboards/${active}/render`)
      .then((r) => setRendered((s) => ({ ...s, [active]: r.data })))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [active, rendered]);

  const refreshActive = async () => {
    if (!active) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/my-dashboards/${active}/render`);
      setRendered((s) => ({ ...s, [active]: data }));
      toast.success("Refreshed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const closeTab = (id) => {
    const idx = tabs.findIndex((t) => t.id === id);
    const next = tabs.filter((t) => t.id !== id);
    setTabs(next);
    if (active === id) {
      setActive(next[Math.max(0, idx - 1)]?.id || null);
    }
    setRendered((s) => {
      const c = { ...s };
      delete c[id];
      return c;
    });
  };

  const openPicker = async () => {
    try {
      const { data } = await api.get("/my-dashboards");
      setAvailable(data.filter((d) => !tabs.some((t) => t.id === d.dashboard_id)));
      setPicker(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addTab = (d) => {
    const next = [...tabs, { id: d.dashboard_id, name: d.name }];
    setTabs(next);
    setActive(d.dashboard_id);
    setPicker(false);
  };

  const downloadActive = async (fmt) => {
    if (!active) return;
    try {
      const res = await fetch(`${API}/my-dashboards/${active}/export/${fmt}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const name = (rendered[active]?.name || "dashboard").replace(/\W+/g, "_");
      a.download = `${name}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const activeData = active ? rendered[active] : null;

  return (
    <>
      <Toaster position="top-right" richColors />
      <Topbar
        title="Dashboard Workspace"
        subtitle={`${tabs.length} open · click + to add another`}
        right={
          active && (
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={refreshActive}
                data-testid="refresh-active"
                className="flex h-9 items-center gap-1.5 rounded-sm border border-slate-200 px-3 text-xs text-slate-600 hover:border-navy-600 hover:text-navy-600"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>
              {["pdf", "xlsx", "csv"].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => downloadActive(f)}
                  data-testid={`download-${f}`}
                  className="flex h-9 items-center gap-1.5 rounded-sm border border-slate-200 px-3 text-xs font-semibold uppercase tracking-widest text-slate-600 hover:border-navy-600 hover:text-navy-600"
                >
                  <Download className="h-3.5 w-3.5" />
                  {f}
                </button>
              ))}
            </div>
          )
        }
      />

      {/* Tab bar */}
      <div className="flex h-11 items-end gap-0.5 border-b border-slate-200 bg-slate-50 px-3" data-testid="tab-bar">
        {tabs.map((t) => (
          <div
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setActive(t.id)}
            className={[
              "group flex h-9 cursor-pointer items-center gap-2 rounded-t-md border border-b-0 px-3 text-xs font-medium transition-colors",
              active === t.id
                ? "border-slate-200 bg-background text-navy-600"
                : "border-transparent text-slate-500 hover:bg-slate-100",
            ].join(" ")}
          >
            <Layers className={`h-3 w-3 ${active === t.id ? "text-brand-orange" : "text-slate-400"}`} />
            <span className="max-w-[180px] truncate">{t.name}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                closeTab(t.id);
              }}
              data-testid={`close-tab-${t.id}`}
              className="rounded-sm p-0.5 text-slate-400 opacity-0 hover:bg-slate-200 hover:text-navy-600 group-hover:opacity-100"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={openPicker}
          data-testid="add-tab-button"
          className="flex h-9 items-center gap-1 rounded-t-md px-3 text-xs text-slate-500 hover:bg-slate-100 hover:text-navy-600"
        >
          <Plus className="h-3.5 w-3.5" />
          Add dashboard
        </button>
      </div>

      {/* Content */}
      <div className="p-6 animate-fade-in" data-testid="workspace-content">
        {!active ? (
          <EmptyState onPick={openPicker} />
        ) : loading && !activeData ? (
          <div className="text-sm text-slate-500">Rendering dashboard…</div>
        ) : activeData ? (
          <DashboardGrid data={activeData} />
        ) : null}
      </div>

      {/* Picker modal */}
      {picker && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy-600/70 p-4 backdrop-blur-sm"
          data-testid="picker-modal"
          onClick={() => setPicker(false)}
        >
          <div
            className="w-full max-w-lg rounded-md border border-slate-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div className="font-display text-base font-semibold text-navy-600">
                Open a dashboard
              </div>
              <button onClick={() => setPicker(false)} className="rounded-sm p-1 text-slate-400 hover:text-navy-600">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto p-2">
              {available.length === 0 && (
                <div className="p-6 text-center text-sm text-slate-500">
                  No more dashboards to open. Already showing all of them.
                </div>
              )}
              {available.map((d) => (
                <button
                  key={d.dashboard_id}
                  type="button"
                  onClick={() => addTab(d)}
                  data-testid={`dashboard-picker-item`}
                  data-dashboard-id={d.dashboard_id}
                  className="flex w-full items-center justify-between gap-3 rounded-sm px-3 py-2.5 text-left hover:bg-slate-50"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-navy-600">{d.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">
                      {d.is_report ? "REPORT" : "DASHBOARD"} · {d.widgets?.length || 0} widgets
                    </div>
                  </div>
                  <Plus className="h-4 w-4 text-brand-orange" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EmptyState({ onPick }) {
  return (
    <div className="rounded-md border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="empty-workspace">
      <Layers className="mx-auto h-10 w-10 text-slate-300" />
      <div className="mt-3 font-display text-lg font-semibold text-navy-600">No dashboards open</div>
      <div className="mt-1 text-sm text-slate-500">
        Click + above to add a saved dashboard, or build a new one.
      </div>
      <div className="mt-4 flex justify-center gap-2">
        <button
          type="button"
          onClick={onPick}
          className="flex h-9 items-center gap-1.5 rounded-sm bg-navy-600 px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-navy-700"
        >
          <Plus className="h-3.5 w-3.5" />
          Add an existing one
        </button>
        <Link
          to="/dashboards/builder"
          className="flex h-9 items-center gap-1.5 rounded-sm bg-brand-orange px-3 text-xs font-semibold uppercase tracking-widest text-white hover:bg-[#C1701C]"
        >
          <Plus className="h-3.5 w-3.5" />
          Build a new one
        </Link>
      </div>
    </div>
  );
}

function DashboardGrid({ data }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold tracking-tight text-navy-600">{data.name}</h2>
          {data.description && <p className="text-sm text-slate-500">{data.description}</p>}
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-400">
          rendered {new Date(data.rendered_at).toLocaleTimeString()}
        </span>
      </div>
      <div className="grid grid-cols-12 gap-3">
        {(data.widgets || []).map((w) => {
          const span = Math.max(1, Math.min(w.w || 4, 12));
          return (
            <div
              key={w.instance_id}
              style={{
                gridColumn: `span ${span} / span ${span}`,
                minHeight: `${(w.h || 1) * 110}px`,
              }}
            >
              <Widget widget={w} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
